import { ts } from '@shared/lib/diagTs';
import { useCallback, useEffect, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Globe, KeyRound, RefreshCw, Server, User, X } from 'lucide-react';
import { useApiClient } from '@core/api/useApiClient';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { useAccessTokenProvider } from '@core/auth/useAccessToken';
import { useRithmicConnect, useRithmicDisconnect } from '../hooks/useRithmicConnect';
import {
  abortRithmicConnection, allowRithmicAutoResume, blockRithmicAutoResume,
  getRithmicRelay, type RithmicRelay, setRithmicReconnectHandler, setRithmicTokenGetter, type RelayConnState,
} from '../lib/rithmicRelay';
import {
  clearStoredRithmicCreds, readStoredRithmicCreds, systemSupportsLocation,
  DEFAULT_RITHMIC_LOCATION, DEFAULT_RITHMIC_SYSTEM, RITHMIC_LOCATIONS, RITHMIC_SYSTEMS,
} from '../lib/rithmicCreds';
import { readRithmicAccountMeta } from '../lib/rithmicAccountMeta';
import { setRithmicHistoryAnchors } from '../lib/rithmicOrderData';
import { readAccountLogin } from '@features/accounts/lib/accountLogins';
import { runRithmicConnectSequence } from '../lib/rithmicConnectFlow';
import { deriveBadgeDisplay, normaliseRithmicState, shouldAutoResume, type RithmicBeState } from '../lib/rithmicUiState';
import { latchReasonForBrokerError, readAutoResumeLatch } from '@core/realtime/autoResumeLatch';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { useRelayOwnership } from '@core/realtime/useRelayOwnership';
import { type RithmicStatus } from '../hooks/useRithmicDisplayState';
import { RithmicAttribution } from './RithmicAttribution';
import { rc } from '../lib/rithmicLog';
import { Button, ErrorMessage } from '@shared/ui';
import { BRAND_GRADIENT, gradientTextStyle } from '@shared/styles/brand';

const gradientText = gradientTextStyle;

// Single-flight guard so the dialog auto-resumes at most once per page load. A fresh page re-initialises
// this module, so it resets naturally.
let _autoResumeTried = false;

/**
 * User-intent disconnect side-effects, shared by this dialog's Disconnect button AND the Accounts
 * panel's Unlink (2026-07-18 review: the bare POST alone lets auto-resume silently reconnect on the
 * next page load, so an Unlink would undo itself). Callers still POST via `useRithmicDisconnect()`.
 */
export function forgetRithmicConnection(): void {
  clearStoredRithmicCreds();
  _autoResumeTried = true;
  rc.log(ts(), '[rithmic-auto] user disconnected - cleared stored creds, auto-resume disabled');
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Row-driven open (Accounts panel): preselect this Rithmic system instead of the stored default. */
  systemHint?: string | null;
  /** Row-driven open: prefill this username (the FE-only per-account login memory) instead of the
   *  stored default; a differing username clears the stored password so the user re-enters it. */
  usernameHint?: string | null;
  /** Called once the dialog has consumed the hints above (so the caller can clear its one-shot hint). */
  onHintConsumed?: () => void;
}

export function RithmicConnectDialog({ open, onClose, systemHint = null, usernameHint = null, onHintConsumed }: Props) {
  const api = useApiClient();
  const realtime = useRealtime();
  const queryClient = useQueryClient();
  const getAccessToken = useAccessTokenProvider();
  const connect = useRithmicConnect();
  const disconnect = useRithmicDisconnect();

  // Browser-relay singleton — owns the WS to the Rithmic ORDER_PLANT + the SignalR hub to /hub/rithmic.
  // token-getter-via-ref + empty deps so we don't tear down + re-create the relay every render.
  const relayRef = useRef<RithmicRelay | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [relayConn, setRelayConn] = useState<RelayConnState>('closed');
  useEffect(() => {
    setRithmicTokenGetter(() => getAccessTokenRef.current());
    const relay = getRithmicRelay();
    relayRef.current = relay;
    setRelayConn(relay.getConnectionState());
    const unsub = relay.onConnectionChange(() => setRelayConn(relay.getConnectionState()));
    // Start the hub on mount so a page refresh reconnects it + asks the BE to resume any still-live
    // session. Shared singleton + idempotent start(). Resuming opens a WS only if the BE still holds a
    // Live session; for a disconnected user it's just an idle hub.
    void relay.start().catch((err) => rc.error(ts(), '[rithmic-dialog] relay.start() on mount failed', err));
    return () => { unsub(); }; // shared app-level singleton — do NOT stop it on unmount
  }, []);

  const status = useQuery({
    queryKey: ['rithmic', 'status'],
    queryFn:  () => api.get<RithmicStatus>('/api/v1/rithmic/status'),
    refetchInterval: open ? 5_000 : 60_000,
  });

  // Per-account backfill anchors (2026-08-25 split retrieval): fetched once per page load (this
  // dialog mounts in the Topbar at page load, before any socket opens) and handed to the hook-free
  // order-data driver, which floors each account's show_fill_history windows at its anchor. A
  // failed/slow fetch is SAFE: the driver just runs today's full creation-bounded backfill.
  const historyAnchors = useQuery({
    queryKey: ['rithmic', 'history-anchors'],
    queryFn: () => api.get<{ anchors: Record<string, number> }>('/api/v1/rithmic/history-anchors'),
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (historyAnchors.data?.anchors) setRithmicHistoryAnchors(historyAnchors.data.anchors);
  }, [historyAnchors.data]);

  // Write each rithmic.state push into the SHARED ['rithmic','status'] cache (same key the poll + badge
  // use), so badge + dialog are ONE source of truth. A terminal Reject/ForcedLogout ('error') latches
  // off auto-resume so we don't hammer Rithmic with the same bad creds on every SignalR blip.
  useEffect(() => {
    return realtime.subscribe('rithmic.state', (payload) => {
      const next = normaliseRithmicState(payload.state);
      const err = payload.lastError ?? null;
      rc.log('%s [rithmic-dialog] realtime rithmic.state push → %s%s', ts(), next, err ? ` (err=${err})` : '');
      // Durable (every tab + reload) since 2026-09-13: a kick (reason kicked/concurrent_session) or a
      // refused logon must not be auto-resumed by a reloaded tab either (D4).
      if (next === 'error') {
        blockRithmicAutoResume(latchReasonForBrokerError(payload.reason));
        getRelayOwnership('rithmic').release(); // a refused / kicked / dead owner is no owner (D1)
      }
      queryClient.setQueryData<RithmicStatus>(['rithmic', 'status'], (prev) => ({
        state: next,
        accounts: payload.accounts ?? prev?.accounts ?? [],
        lastError: err,
        reason: payload.reason ?? null,
      }));
    });
  }, [realtime, queryClient]);

  // Persisted creds (FE-owned) — see readStoredRithmicCreds / CREDS_STORAGE_KEY.
  const initialCreds = readStoredRithmicCreds();
  const [system, setSystem] = useState(initialCreds.system || DEFAULT_RITHMIC_SYSTEM);
  /** Chosen point of presence (2026-07-29 — Rithmic released the POP list on conformance). */
  const [location, setLocation] = useState(initialCreds.location || DEFAULT_RITHMIC_LOCATION);
  const [username, setUsername] = useState(initialCreds.username);
  const [password, setPassword] = useState(initialCreds.password);
  const [relayStarting, setRelayStarting] = useState(false);

  // Re-seed the form from storage EVERY time the dialog opens (2026-07-18 review, mirrors the CQG
  // dialog): this instance is mounted at page load, so its state can be stale — e.g. the Add-account
  // dialog just stored fresh creds/system; connecting with mount-time state would clobber them.
  // Row-driven opens (Accounts panel) pass system/username hints that WIN over the stored default so
  // Connect on a specific account preselects its system + prefills the username last used for it; a
  // hint username that differs from the stored one clears the password so the user re-enters it.
  useEffect(() => {
    if (!open) return;
    const stored = readStoredRithmicCreds();
    setSystem(systemHint || stored.system || DEFAULT_RITHMIC_SYSTEM);
    setLocation(stored.location || DEFAULT_RITHMIC_LOCATION);
    setUsername(usernameHint ?? stored.username);
    setPassword(usernameHint && usernameHint !== stored.username ? '' : stored.password);
    if (systemHint || usernameHint) onHintConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── FE-driven auto-resume on page load ─────────────────────────────────────
  // Once we know the BE state: if there's NO live session but we DO have stored creds, reconnect
  // automatically so a refresh restores the session without re-prompting. Single-flight (module guard).
  useEffect(() => {
    const beState = status.data?.state;
    if (beState === undefined) return;      // wait for the first /rithmic/status response
    if (_autoResumeTried) return;
    // A rejected logon is terminal — do NOT auto-resume into a guaranteed re-rejection. Likewise a
    // durable latch (a kick, a rejection or the user's own Disconnect recorded by any tab — D4).
    const latch = readAutoResumeLatch('rithmic');
    if (!shouldAutoResume(beState, latch)) {
      _autoResumeTried = true;
      rc.log('%s [rithmic-auto] BE state=%s latch=%s - not auto-resuming; user must Connect', ts(), beState, latch ?? 'none');
      return;
    }
    _autoResumeTried = true;
    const creds = readStoredRithmicCreds();
    const hasCreds = !!(creds.username && creds.password);
    rc.log('%s [rithmic-auto] page load: beState=%s relayConn=%s hasStoredCreds=%s', ts(), beState, relayConn, hasCreds);
    if (relayConn !== 'closed') {
      rc.log('%s [rithmic-auto] socket already %s - no resume needed', ts(), relayConn);
      return;
    }
    if (!hasCreds) {
      rc.log(ts(), '[rithmic-auto] no stored creds - cannot auto-resume; user must Connect');
      return;
    }
    rc.log(ts(), '[rithmic-auto] socket closed - auto-connecting (credential-less POST; FE logons at socket-open)');
    void (async () => {
      // D1: a reloaded tab is the last to ask — claim the connection (the current owner yields first).
      await getRelayOwnership('rithmic').claim();
      try { await relayRef.current?.start(); } catch (e) { rc.error(ts(), '[rithmic-auto] relay start failed', e); }
      try { await connect.mutateAsync(); } catch (e) { rc.error(ts(), '[rithmic-auto] auto-connect failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.state]);

  // ── Silent re-establish when the BE signals it lost our session (RithmicReconnectNeeded) ─────────
  const resumeWithStoredCreds = useCallback(async () => {
    const creds = readStoredRithmicCreds();
    if (!creds.username || !creds.password) {
      rc.log(ts(), '[rithmic-reconnect] BE lost our session - no stored creds; awaiting user Connect');
      return;
    }
    if (connect.isPending) return;
    rc.log(ts(), '[rithmic-reconnect] BE lost our session - silently re-establishing (credential-less connect)');
    await getRelayOwnership('rithmic').claim();
    try { await relayRef.current?.start(); } catch (e) { rc.error(ts(), '[rithmic-reconnect] relay start failed', e); }
    try { await connect.mutateAsync(); } catch (e) { rc.error(ts(), '[rithmic-reconnect] silent connect failed', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect.isPending]);
  const resumeRef = useRef(resumeWithStoredCreds);
  resumeRef.current = resumeWithStoredCreds;
  useEffect(() => {
    setRithmicReconnectHandler(() => { void resumeRef.current(); });
  }, []);

  // On open, refetch so a dialog closed for a while (60s cadence) shows fresh state immediately.
  useEffect(() => {
    if (open) void status.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const state: RithmicBeState = normaliseRithmicState(status.data?.state);
  const lastError = status.data?.lastError ?? null;
  const accounts = status.data?.accounts ?? [];
  // Derive the dialog's view from the SAME function + inputs as the topbar badge, so the icon + window
  // are ALWAYS on the same page: live → Connected; connecting → Connecting; else → connect form.
  const ownership = useRelayOwnership('rithmic');
  const passive = ownership.mode === 'passive';
  const display = deriveBadgeDisplay(state, relayConn, passive);
  const showConnected = display === 'live';
  const showConnecting = display === 'connecting';
  // "Connecting…" reflects an in-flight attempt FROM THIS TAB only — never a stale BE state.
  const isConnecting = connect.isPending || relayStarting || relayConn === 'opening';

  // Escape-to-close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Connect: store creds (the ONLY place they go — never our BE), start the relay hub, then fire the
  // credential-less POST. The relay reads the creds at socket-open to build + send the RequestLogin.
  // The sequence itself lives in `runRithmicConnectSequence` so the Add-account dialog runs the exact
  // same safety steps (auto-resume re-arm, stash-first, relay-before-POST).
  const doConnect = async () => {
    setRelayStarting(true);
    try {
      await runRithmicConnectSequence({
        // The Test system lives on the single dev-kit host, so a POP choice cannot apply to it —
        // send none rather than one the BE would have to ignore.
        creds: { system, username, password, location: systemSupportsLocation(system) ? location : '' },
        mutate: () => connect.mutateAsync(),
      });
    } catch (err) {
      // Swallow the rejection: the connect POST's failure is surfaced to the user via connect.isError
      // (rendered as <ErrorMessage>), and leaving it uncaught bubbles an unhandled promise rejection
      // out of the form onSubmit handler.
      rc.error(ts(), '[rithmic-dialog] connect POST failed', err);
    } finally {
      setRelayStarting(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await doConnect();
  };

  // "Refresh" from the CONNECTED view: re-arm the session — relay hub up, then a credential-less
  // /rithmic/connect; the relay re-Logons from the creds ALREADY in localStorage. Deliberately NOT
  // doConnect (which would clobber the stored creds with this view's empty form fields).
  const refresh = async () => {
    allowRithmicAutoResume();
    try {
      await relayRef.current?.start();
    } catch (err) {
      rc.error(ts(), '[rithmic-dialog] refresh: relay.start() failed', err);
    }
    try {
      await connect.mutateAsync();
    } catch (err) {
      rc.error(ts(), '[rithmic-dialog] refresh: connect POST failed', err);
    }
  };

  // Cancel from the CONNECTING view = ABORT the not-yet-live attempt: drop the BE session AND stop this
  // tab's relay so the socket doesn't keep re-opening. The dialog STAYS OPEN (user direction
  // 2026-07-18): the abort drives the state to disconnected, which re-renders this dialog as the
  // credentials form — Cancel returns you to the login screen, not out of the flow.
  const cancelConnecting = useCallback(() => {
    void abortRithmicConnection({
      disconnect: () => disconnect.mutateAsync(),
      stopRelay: () => relayRef.current?.stop() ?? Promise.resolve(),
    });
  }, [disconnect]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--scrim)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Connect your Rithmic account"
        onClick={e => e.stopPropagation()}
        className="flex w-full max-w-[564px] flex-col gap-4 rounded-2xl bg-canvas-raised p-6 shadow-card"
        style={{ border: '1px solid var(--canvas-divider)' }}
      >
        <header className="flex items-center justify-between gap-2.5">
          <h2 className="flex items-center gap-2 text-base font-bold text-text">
            <span aria-hidden>🔌</span> {showConnecting ? 'Connecting to Rithmic' : showConnected ? (passive ? 'Connected to Rithmic in another tab' : 'Currently connected to Rithmic') : 'Connect your Rithmic account'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-lg border bg-canvas-raised text-text-muted transition-colors hover:text-text"
            style={{ borderColor: 'var(--canvas-divider)' }}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {showConnecting ? (
          /* ── Connecting / resuming — mirrors the badge's "connecting" so icon + window agree ── */
          <ConnectingView onCancel={cancelConnecting} />
        ) : !showConnected ? (
          /* ── Connect form: system dropdown + username + password ───────────── */
          <form onSubmit={submit} id="rithmic-connect-form" className="flex flex-col gap-4">
            <p className="text-[13px] leading-5 text-text-muted">
              Link your Rithmic account to sync your trades automatically.
            </p>

            <RithmicSelect icon={<Server className="h-5 w-5" />} value={system} onChange={setSystem} aria-label="Rithmic system">
              {RITHMIC_SYSTEMS.map(s => <option key={s} value={s}>{s}</option>)}
            </RithmicSelect>

            {/* Point of presence (2026-07-29): every production gateway serves every system, so this
                is purely which one to dial — pick the closest for latency. It is DISABLED rather than
                hidden for "Rithmic Test" (2026-07-30: hiding it read as "the feature is missing"),
                which runs on the single dev-kit host and must keep dialing the test URL. */}
            <div className="flex flex-col gap-1">
              <RithmicSelect
                icon={<Globe className="h-5 w-5" />}
                value={systemSupportsLocation(system) ? location : DEFAULT_RITHMIC_LOCATION}
                onChange={setLocation}
                aria-label="Rithmic connection location"
                disabled={!systemSupportsLocation(system)}
              >
                {RITHMIC_LOCATIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </RithmicSelect>
              <span className="px-1 text-[12px] leading-4 text-text-subtle">
                {systemSupportsLocation(system)
                  ? 'Connection location: all locations reach the same accounts; pick the one nearest you.'
                  : 'Rithmic Test runs on a single test gateway, so there is no location to choose.'}
              </span>
            </div>

            <RithmicInput
              icon={<User className="h-5 w-5" />}
              type="text"
              value={username}
              onChange={setUsername}
              placeholder="Username"
              autoComplete="username"
              required
            />
            <RithmicInput
              icon={<KeyRound className="h-5 w-5" />}
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="Password"
              autoComplete="current-password"
              required
            />

            {connect.isError && <ErrorMessage error={connect.error} />}
            {lastError && !isConnecting && (
              <div className="text-[13px]" style={{ color: 'var(--loss)' }}>{lastError}</div>
            )}

            <p className="text-[12px] leading-4 text-text-subtle">
              🔒 Your credentials stay in this browser: they are used only to log in to Rithmic directly
              and are never sent to or stored on our servers.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
                style={gradientText}
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isConnecting}
                className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT }}
              >
                {isConnecting ? 'Connecting…' : 'Connect'}
              </button>
            </div>
          </form>
        ) : (
          /* ── Connected: account(s) + Refresh + Close + Disconnect ──────────── */
          <div className="flex flex-col gap-4">
            {accounts.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted">
                  {accounts.length === 1 ? 'Account' : 'Accounts'}
                </div>
                <ul className="mt-1 space-y-2 text-sm">
                  {accounts.map(a => <RithmicAccountItem key={a} id={a} />)}
                </ul>
              </div>
            )}

            <Button
              variant="secondary"
              fullWidth
              loading={connect.isPending}
              onClick={() => void refresh()}
              icon={<RefreshCw className="h-4 w-4" />}
            >
              {connect.isPending ? 'Refreshing…' : 'Refresh'}
            </Button>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
                style={gradientText}
              >
                Close
              </button>
              <button
                type="button"
                disabled={disconnect.isPending}
                onClick={() => {
                  // Explicit disconnect = a real logoff: forget the stored creds and block auto-resume
                  // so we don't silently reconnect on the next page load. Shared with the Accounts
                  // panel's Unlink (forgetRithmicConnection).
                  forgetRithmicConnection();
                  disconnect.mutate();
                }}
                className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT }}
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        )}

        {/* Rithmic/OMNE attribution (conformance requirement) — this dialog IS the Rithmic window. */}
        <RithmicAttribution />
      </div>
    </div>,
    document.body,
  );
}

/** "Connecting…" view — shown when the badge derivation reads 'connecting'. Mirrors the topbar badge. */
function ConnectingView({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col items-center gap-4 py-2">
        <p className="text-center text-[13px] font-semibold leading-4 text-text">Connecting to Rithmic…</p>
        <p className="text-center text-[13px] leading-5 text-text-muted">Re-establishing the live connection.</p>
        <div className="h-1 w-full max-w-[238px] overflow-hidden rounded-full bg-canvas-inset">
          <div className="h-full w-full animate-pulse rounded-full" style={{ background: BRAND_GRADIENT }} />
        </div>
      </div>
      <div className="flex w-full items-center justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={gradientText}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * One connected-account row. Enriches the bare Rithmic account id with the account-level fields the
 * browser captured from ResponseAccountList (name / FCM / IB / currency — FE-only meta store) plus the
 * email + system from the per-account login memory (session creds as fallback). Rithmic exposes no
 * BE-side friendly name (unlike CQG's accounts registry), so this is the only source; it falls back to
 * the raw id before the first account-list frame is captured. Never renders the password. (2026-07-22)
 */
function RithmicAccountItem({ id }: { id: string }) {
  const meta = readRithmicAccountMeta(id);
  const login = readAccountLogin(id);
  const creds = readStoredRithmicCreds();
  const name = meta?.accountName || id;
  const system = login?.system || creds.system;
  const email = login?.username || creds.username;
  const routing = [
    meta?.fcmId && `FCM ${meta.fcmId}`,
    meta?.ibId && `IB ${meta.ibId}`,
    meta?.currency,
  ].filter(Boolean).join(' · ');
  return (
    <li className="flex flex-col gap-0.5">
      <span className="text-sm font-semibold text-text">{name}</span>
      <span className="text-xs text-text-muted">
        <span className="font-mono">{id}</span>{system ? ` · ${system}` : ''}
      </span>
      {email && <span className="text-xs text-text-muted">{email}</span>}
      {routing && <span className="text-xs text-text-subtle">{routing}</span>}
    </li>
  );
}

/**
 * Bordered input pill with a leading icon (matches the CQG dialog's Text-Input: 48px tall, 1.5px
 * border, 12px radius). Colours come from theme tokens so it works in dark/light.
 */
function RithmicInput({
  icon, value, onChange, type, ...rest
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  // Password fields get a trailing show/hide eye toggle (2026-07-18 user direction).
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="flex h-12 items-center gap-3 rounded-xl border-[1.5px] border-[color:var(--canvas-divider)] bg-canvas-raised px-3 transition-colors focus-within:border-[color:var(--accent)]">
      <span className="text-text-muted">{icon}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        type={isPassword && revealed ? 'text' : type}
        className="w-full bg-transparent text-[13px] text-text outline-none placeholder:text-text-muted"
        {...rest}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setRevealed(r => !r)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          className="text-text-muted transition-colors hover:text-text"
        >
          {revealed ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      )}
    </label>
  );
}

/** Bordered select pill with a leading icon — the system dropdown, styled to match RithmicInput. */
function RithmicSelect({
  icon, value, onChange, children, disabled, ...rest
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  return (
    // The whole pill dims when disabled, so an inert control reads as inert rather than broken.
    <label
      className="flex h-12 items-center gap-3 rounded-xl border-[1.5px] border-[color:var(--canvas-divider)] bg-canvas-raised px-3 transition-colors focus-within:border-[color:var(--accent)]"
      style={disabled ? { opacity: 0.55 } : undefined}
    >
      <span className="text-text-muted">{icon}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full bg-transparent text-[13px] text-text outline-none enabled:cursor-pointer disabled:cursor-not-allowed"
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}
